// Component: ModelsPage.jsx
// Purpose: Superuser model training, lifecycle, scorecard, drift, and reference controls via the gateway.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiBox,
  FiCheckCircle,
  FiClock,
  FiPlay,
  FiTrendingUp,
} from "react-icons/fi";

import { Badge, Button, DataTable, PageHeader, Segmented, Sparkline } from "../../../components/ds";
import InstrumentSelector from "../../../components/common/InstrumentSelector";
import { useAuth } from "../../../contexts/AuthContext";
import { services } from "../../../services/api";
import { unwrapModelPayload, unwrapModelVersions } from "../../../services/modelVersionsEnvelope";
import ModelPerformanceCharts from "./ModelPerformanceCharts";

const STATUS_OPTIONS = ["all", "candidate", "passed", "promoted", "retired"];
const TIMEFRAME_OPTIONS = ["1m", "5m", "15m", "1h"];
const TERMINAL_TRAINING_STATUSES = new Set(["passed", "promoted", "retired", "failed", "rejected", "completed"]);

const statusVariant = {
  candidate: "neutral",
  passed: "info",
  promoted: "profit",
  retired: "warn",
  failed: "loss",
  rejected: "loss",
};

const dateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Kolkata",
  timeZoneName: "short",
});

function responseStatus(error) {
  return error?.response?.status ?? error?.status;
}

function titleCase(value) {
  if (value === null || value === undefined || value === "") return "—";
  const text = String(value);
  return text.charAt(0).toUpperCase() + text.slice(1).replaceAll("_", " ");
}

function formatDate(value, includeTime = false) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return (includeTime ? dateTimeFormatter : dateFormatter).format(parsed);
}

function formatPercent(fraction) {
  if (fraction === null || fraction === undefined || Number.isNaN(Number(fraction))) {
    return "—";
  }
  const percent = Number(fraction) * 100;
  const rounded = Math.round((percent + Number.EPSILON) * 10) / 10;
  return `${rounded.toFixed(1)}%`;
}

function formatFixed(value, digits) {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(digits);
}

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function requestId(prefix) {
  const suffix = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${suffix}`;
}

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (typeof detail?.message === "string") return detail.message;
  return error?.message || fallback;
}

function modelTypeLabel(capability) {
  if (!capability || typeof capability !== "object") return titleCase(capability);
  return capability.title || titleCase(capability.head || capability.id);
}

function familiesText(capability) {
  const families = Array.isArray(capability?.families) ? capability.families : [];
  return families.length ? families.join(" + ") : "";
}

//--> A version's type is the capability whose head matches config.head (null head = regime); list rows carry head, detail carries config.head.
function capabilityForVersion(model, trainingTypes) {
  const head = model?.head ?? model?.config?.head ?? null;
  return (trainingTypes || []).find((type) => (type.head || null) === head) || null;
}

function isQualified(model) {
  return model?.status === "passed" || model?.verdict === "passed" || model?.scorecard?.verdict === "passed";
}

function verdictReason(model) {
  return model?.verdict_reason || model?.scorecard?.verdict_reason || null;
}

function firstMetric(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== null && value !== undefined && Number.isFinite(Number(value))) return Number(value);
  }
  return null;
}

function StatusBadge({ status }) {
  return (
    <Badge variant={statusVariant[status] || "neutral"} size="sm" dot>
      {titleCase(status)}
    </Badge>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg border border-[var(--divider)] bg-bg-3/40 p-3">
      <p className="text-[11px] uppercase tracking-wide text-fg-3">{label}</p>
      <p className="mt-1 text-sm font-semibold tabular-nums text-fg-1">{value}</p>
    </div>
  );
}

function HeadlineMetrics({ scorecard }) {
  const skill = firstMetric(scorecard, ["balanced_accuracy", "oos_accuracy"]);
  const baseline = firstMetric(scorecard, [
    "baseline_balanced_accuracy",
    "majority_rate",
    "control_oos_accuracy",
  ]);
  const pnl = firstMetric(scorecard, ["total_pnl", "total_points"]);
  if (skill === null && baseline === null && pnl === null) return <span className="text-fg-3">—</span>;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums text-fg-2">
      {skill !== null && <span>{scorecard?.balanced_accuracy !== undefined ? "Skill" : "OOS"} {formatPercent(skill)}</span>}
      {baseline !== null && <span>Baseline {formatPercent(baseline)}</span>}
      {pnl !== null && <span>P&amp;L {formatFixed(pnl, 2)}</span>}
    </div>
  );
}

export function ScorecardPanel({ scorecard }) {
  const profile = scorecard ? [
    Number(scorecard.oos_accuracy || 0),
    Number(scorecard.control_oos_accuracy || 0),
    Math.max(0, 1 - Number(scorecard.calibration_ece || 0)),
  ] : [];
  return (
    <section aria-labelledby="scorecard-heading" className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <h3 id="scorecard-heading" className="text-sm font-semibold text-fg-1">
        Scorecard
      </h3>
      {!scorecard ? (
        <p className="mt-4 text-sm text-fg-3">Not yet scored</p>
      ) : (
        <>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Metric label="OOS accuracy" value={formatPercent(scorecard.oos_accuracy)} />
          <Metric label="Control accuracy" value={formatPercent(scorecard.control_oos_accuracy)} />
          <Metric
            label="Calibration ECE"
            value={formatFixed(scorecard.calibration_ece, 3)}
          />
          <Metric
            label="Parity"
            value={
              scorecard.parity_check === null || scorecard.parity_check === undefined ? (
                "—"
              ) : (
                <Badge variant={scorecard.parity_check ? "profit" : "loss"} size="xs">
                  {scorecard.parity_check ? "Pass" : "Fail"}
                </Badge>
              )
            }
          />
          <Metric
            label="Inference p99"
            value={
              formatFixed(scorecard.inference_latency_p99_ms, 1) === "—"
                ? "—"
                : `${formatFixed(scorecard.inference_latency_p99_ms, 1)} ms`
            }
          />
        </div>
        <div
          role="img"
          aria-label="Model performance profile"
          className="mt-4 rounded-lg border border-[var(--divider)] bg-bg-3/40 p-3"
        >
          <div className="mb-2 flex justify-between text-[10px] uppercase tracking-wide text-fg-3">
            <span>OOS</span><span>Control</span><span>Calibration quality</span>
          </div>
          <Sparkline data={profile} width={640} height={72} tint="auto" fill lastDot className="h-16 w-full" />
        </div>
        </>
      )}
    </section>
  );
}

function hasPerformanceData(performance) {
  if (!performance) return false;
  return [
    performance.paper_pnl,
    performance.backtest_pnl,
    performance.paper_trades,
    performance.backtest_trades,
    performance.rolling_accuracy,
    performance.drift_checked_on,
    performance.drift_state,
  ].some((value) => value !== null && value !== undefined);
}

//! A-4: the drift badge shows only for a CHECKED reading (belt and braces with the API reader).
function showsDrift(performance) {
  return performance?.drift_state === "checked" && performance?.drift_flag === true;
}

// -->  A-16: human label for the API's excluded reason; an unknown reason is shown as sent
function excludedLabel(reason) {
  if (!reason) return "—";
  if (reason.startsWith("drift_metric_v1_only")) return "Excluded — newer model schema";
  if (reason === "bench_row") return "Excluded — scored by its head bench";
  return `— (${reason})`;
}

//* Rolling accuracy is shown only for drift_state "checked"; the browser computes no state (WP4).
function rollingAccuracyDisplay(performance) {
  switch (performance?.drift_state) {
    case "checked":
      return formatPercent(performance.rolling_accuracy);
    case "stale":
      return `Stale — last scored ${formatDate(performance.drift_as_of, true)}`;
    case "insufficient":
      return `Insufficient — ${performance.drift_sessions ?? "?"} sessions`;
    case "unscored":
      return "Unscored";
    // -->  QA-ARCH-12: the API's reason sits beside the dash / label (no browser rule)
    case "unavailable":
      return performance.drift_reason ? `Unavailable — ${performance.drift_reason}` : "Unavailable";
    case "excluded":
      return excludedLabel(performance.drift_reason);
    default:
      return "—";
  }
}

export function PerformancePanel({ performance }) {
  const hasData = hasPerformanceData(performance);
  return (
    <section aria-labelledby="performance-heading" className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 id="performance-heading" className="text-sm font-semibold text-fg-1">
          Performance &amp; drift
        </h3>
        {showsDrift(performance) && (
          <Badge variant="loss" size="sm" dot>
            Drift detected
          </Badge>
        )}
      </div>
      {!hasData ? (
        <p className="mt-4 text-sm text-fg-3">No data yet</p>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Metric label="Rolling accuracy" value={rollingAccuracyDisplay(performance)} />
          <Metric label="Paper P&amp;L" value={formatMoney(performance.paper_pnl)} />
          <Metric label="Backtest P&amp;L" value={formatMoney(performance.backtest_pnl)} />
          <Metric label="Paper trades" value={performance.paper_trades ?? "—"} />
          <Metric label="Backtest trades" value={performance.backtest_trades ?? "—"} />
          <Metric label="Observed days" value={performance.days ?? "—"} />
        </div>
      )}
    </section>
  );
}

export function TransitionTimeline({ transitions = [] }) {
  return (
    <section
      aria-label="Transition timeline"
      className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4"
    >
      <h3 className="text-sm font-semibold text-fg-1">Transition timeline</h3>
      {transitions.length === 0 ? (
        <p className="mt-4 text-sm text-fg-3">No transitions recorded</p>
      ) : (
        <ol className="mt-4 space-y-3 border-l border-[var(--divider)] pl-4">
          {transitions.map((transition, index) => (
            <li key={`${transition.created_at || "transition"}-${index}`} className="relative">
              <span className="absolute -left-[19px] top-1.5 h-2 w-2 rounded-full bg-[var(--theme-purple)]" />
              <div className="flex flex-wrap items-center gap-2 text-sm text-fg-1">
                <span>{titleCase(transition.from_status || "created")}</span>
                <span aria-hidden="true" className="text-fg-3">→</span>
                <StatusBadge status={transition.to_status} />
              </div>
              <p className="mt-1 text-xs text-fg-3">
                {formatDate(transition.created_at, true)}
                {transition.reason ? ` · ${transition.reason}` : ""}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function referenceName(reference) {
  return reference.name || "Unnamed strategy";
}

function VersionCell({ value }) {
  return <span>v{value ?? "—"}</span>;
}

export function ReferencesPanel({ references = {} }) {
  const strategies = references.strategies || [];
  const deployments = references.deployments || [];

  return (
    <section aria-labelledby="references-heading" className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <h3 id="references-heading" className="text-sm font-semibold text-fg-1">
        References
      </h3>

      <div className="mt-4 space-y-5">
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-3">
            Strategies
          </h4>
          {strategies.length === 0 ? (
            <p className="text-sm text-fg-3">No referencing strategies</p>
          ) : (
            <div className="overflow-x-auto">
              <DataTable dense>
                <DataTable.Head>
                  <DataTable.Row>
                    <DataTable.HeaderCell>Strategy</DataTable.HeaderCell>
                    <DataTable.HeaderCell>Version</DataTable.HeaderCell>
                  </DataTable.Row>
                </DataTable.Head>
                <DataTable.Body>
                  {strategies.map((strategy, index) => (
                    <DataTable.Row key={`${referenceName(strategy)}-${strategy.version_number}-${index}`}>
                      <DataTable.Cell>{referenceName(strategy)}</DataTable.Cell>
                      <DataTable.Cell><VersionCell value={strategy.version_number} /></DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable>
            </div>
          )}
        </div>

        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-3">
            Deployments
          </h4>
          {deployments.length === 0 ? (
            <p className="text-sm text-fg-3">No active deployments</p>
          ) : (
            <div className="overflow-x-auto">
              <DataTable dense>
                <DataTable.Head>
                  <DataTable.Row>
                    <DataTable.HeaderCell>Strategy</DataTable.HeaderCell>
                    <DataTable.HeaderCell>Version</DataTable.HeaderCell>
                    <DataTable.HeaderCell>Mode</DataTable.HeaderCell>
                    <DataTable.HeaderCell>Status</DataTable.HeaderCell>
                  </DataTable.Row>
                </DataTable.Head>
                <DataTable.Body>
                  {deployments.map((deployment) => (
                    <DataTable.Row key={deployment.deployment_id}>
                      <DataTable.Cell>{referenceName(deployment)}</DataTable.Cell>
                      <DataTable.Cell><VersionCell value={deployment.version_number} /></DataTable.Cell>
                      <DataTable.Cell>{titleCase(deployment.mode)}</DataTable.Cell>
                      <DataTable.Cell><StatusBadge status={deployment.status} /></DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function ModelDetailPanel({
  model,
  references,
  onTransition,
  transitionBusy = false,
  transitionError = null,
  trainingTypes = [],
}) {
  if (!model) return null;
  const typeInfo = capabilityForVersion(model, trainingTypes);
  return (
    <div className="mt-6 space-y-4">
      {/* A-3: the detail response carries the same instrument_lookup_degraded flag as the list */}
      {model.instrument_lookup_degraded === true && <DegradedBanner testId="instrument-lookup-degraded-detail" />}
      <div className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-fg-3">Selected model</p>
            <h2 className="mt-1 text-lg font-bold text-fg-1">{model.version_label}</h2>
            {typeInfo && (
              <div data-testid="model-type-help" className="mt-1 text-sm text-fg-2">
                <p className="font-semibold">{typeInfo.title}</p>
                <p className="text-fg-3">{typeInfo.description}</p>
                {typeInfo.outputs_help && Object.keys(typeInfo.outputs_help).length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-fg-3">
                    {Object.entries(typeInfo.outputs_help).map(([name, help]) => (
                      <li key={name}><code>{name}</code>: {help}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <p className="mt-1 text-sm text-fg-3">
              {titleCase(model.model_family)} · {model.timeframe} · {model.instrument_symbol || "Instrument unavailable"}
              {model.instrument_exchange ? ` / ${model.instrument_exchange}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={model.status} />
            {showsDrift(model.performance) && (
              <Badge variant="loss" size="sm" dot>Drift</Badge>
            )}
            {model.status === "passed" && (
              <Button size="sm" disabled={transitionBusy} onClick={() => onTransition?.(model.id, "promoted")}>
                Promote
              </Button>
            )}
            {model.status === "promoted" && (
              <Button size="sm" variant="danger" disabled={transitionBusy} onClick={() => onTransition?.(model.id, "retired")}>
                Retire
              </Button>
            )}
          </div>
        </div>
      </div>
      {transitionError && (
        <p role="alert" aria-label="Transition error" className="rounded-lg border border-loss/30 bg-loss/5 p-3 text-sm text-loss">
          {transitionError}
        </p>
      )}
      <ScorecardPanel scorecard={model.scorecard} />
      <ModelPerformanceCharts scorecard={model.scorecard} />
      <PerformancePanel performance={model.performance} />
      <div className="grid gap-4 xl:grid-cols-2">
        <TransitionTimeline transitions={model.transitions || []} />
        <ReferencesPanel references={references} />
      </div>
    </div>
  );
}

function progressLabel(result, trainingTypes) {
  const head = result.head || null;
  const type = (trainingTypes || []).find((item) => (item.head || null) === head);
  const family = result.model_family || result.family;
  return [type?.title || "Model", family && titleCase(family)].filter(Boolean).join(" · ");
}

function TrainingPanel({ trainingTypes, onTrain, busy, job, error }) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [instrument, setInstrument] = useState(null);
  const [timeframe, setTimeframe] = useState("5m");
  const [rolloverDays, setRolloverDays] = useState(2);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [replayRunId, setReplayRunId] = useState("");
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    setSelectedTypes((current) => {
      const availableIds = trainingTypes.map((type) => type.id);
      const stillAvailable = current.filter((value) => availableIds.includes(value));
      return stillAvailable.length > 0 ? stillAvailable : availableIds;
    });
  }, [trainingTypes]);

  const replayRequired = trainingTypes.some((type) => (
    type.requires_replay === true && selectedTypes.includes(type.id)
  ));
  const canTrain = instrument?.instrument_id && timeframe && selectedTypes.length > 0
    && (!replayRequired || replayRunId);

  //--> Finished backtests to learn entries from (only fetched when entry success is chosen)
  useEffect(() => {
    if (!replayRequired) return undefined;
    let cancelled = false;
    services.backtesting.listRuns({ page_size: 100 })
      .then((response) => {
        const data = response?.data?.data || response?.data || {};
        const list = (data.runs || []).filter((run) => run.status === "completed");
        if (!cancelled) setRuns(list);
      })
      .catch(() => { if (!cancelled) setRuns([]); });
    return () => { cancelled = true; };
  }, [replayRequired]);

  const toggleType = (type) => {
    setSelectedTypes((current) => current.includes(type)
      ? current.filter((value) => value !== type)
      : [...current, type]);
  };

  return (
    <section className="mb-6 rounded-xl border border-[var(--divider)] bg-bg-2 p-4" aria-labelledby="train-models-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="train-models-heading" className="font-semibold text-fg-1">Train models</h2>
          <p className="text-sm text-fg-3">Choose an instrument, timeframe and model types. Data is cleaned and checked automatically before training; models that pass are marked Passed and can be promoted.</p>
        </div>
        <Button disabled={!canTrain || busy} onClick={() => onTrain({
          instrument_id: instrument.instrument_id,
          timeframe,
          rollover_days: Number(rolloverDays),
          training_types: selectedTypes,
          ...(replayRequired ? { replay_run_id: replayRunId } : {}),
        })}>
          <FiPlay aria-hidden="true" /> {busy ? "Starting…" : "Train"}
        </Button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <button
          type="button"
          aria-label="Select training instrument"
          className="rounded-lg border border-[var(--divider)] bg-bg-3 px-3 py-2 text-left text-sm text-fg-1"
          onClick={() => setSelectorOpen(true)}
        >
          {instrument ? `${instrument.symbol || instrument.name} / ${instrument.exchange}` : "Select instrument"}
        </button>
        <label className="text-xs font-semibold text-fg-3">
          Training timeframe
          <select aria-label="Training timeframe" value={timeframe} onChange={(event) => setTimeframe(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--divider)] bg-bg-3 px-3 py-2 text-sm text-fg-1">
            {TIMEFRAME_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-fg-3">
          Rollover days
          <input aria-label="Rollover days" type="number" min="0" max="30" value={rolloverDays} onChange={(event) => setRolloverDays(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--divider)] bg-bg-3 px-3 py-2 text-sm text-fg-1" />
          <span className="mt-1 block font-normal text-fg-3">Trading days before expiry when the series switches to the next contract (2 = default)</span>
        </label>
      </div>

      <fieldset className="mt-4">
        <legend className="text-xs font-semibold uppercase tracking-wide text-fg-3">Model types</legend>
        <div className="mt-2 grid gap-3 md:grid-cols-3">
          {trainingTypes.map((type) => (
            <label
              key={type.id}
              data-testid={`type-card-${type.id}`}
              className={`flex cursor-pointer gap-2 rounded-lg border p-3 text-sm text-fg-2 ${selectedTypes.includes(type.id) ? "border-[var(--badge-role-fg)] bg-bg-3" : "border-[var(--divider)]"}`}
            >
              <input type="checkbox" aria-label={modelTypeLabel(type)} checked={selectedTypes.includes(type.id)} onChange={() => toggleType(type.id)} />
              <span>
                <span className="block font-semibold text-fg-1">{modelTypeLabel(type)}</span>
                {type.description && <span className="mt-1 block text-xs text-fg-3">{type.description}</span>}
                {familiesText(type) && <span className="mt-1 block text-xs text-fg-3">Trains: {familiesText(type)}, and their ensemble</span>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {replayRequired && (
        <div className="mt-4">
          <label className="text-xs font-semibold text-fg-3">Entry success backtest
            <select aria-label="Entry success backtest" value={replayRunId} onChange={(event) => setReplayRunId(event.target.value)} className="mt-1 w-full rounded-lg border border-[var(--divider)] bg-bg-3 px-3 py-2 text-sm text-fg-1">
              <option value="">Select a finished backtest</option>
              {runs.map((run) => (
                <option key={run.run_id} value={run.run_id}>
                  {`${run.strategy_name || "Strategy"} · ${formatDate(run.start_date)} – ${formatDate(run.end_date)} · ${run.metrics?.total_trades ?? "?"} trades`}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-1 text-xs text-fg-3">Pick a finished backtest of the strategy whose entries this model should learn to filter.</p>
        </div>
      )}

      {job && (
        <div className="mt-4 rounded-lg border border-[var(--divider)] bg-bg-3/40 p-3" aria-live="polite">
          <div className="flex items-center gap-2 text-sm font-semibold text-fg-1">
            {job.completed ? <FiCheckCircle className="text-profit" /> : <FiClock className="text-warning" />}
            {job.completed ? "Completed" : "Training in progress"}
          </div>
          <ul className="mt-2 space-y-1 text-xs text-fg-2">
            {job.results.map((result) => (
              <li key={`${result.model_version_id || result.id || result.job_id || "model"}-${result.bench_id || result.job_id || "bench"}`}>
                {progressLabel(result, trainingTypes)}: {titleCase(result.current_status || result.status)}
                {result.verdict_reason ? ` · ${titleCase(result.verdict_reason)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p role="alert" aria-label="Training error" className="mt-4 rounded-lg border border-loss/30 bg-loss/5 p-3 text-sm text-loss">
          {error}
        </p>
      )}

      <InstrumentSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={(next) => { setInstrument(next?.instrument || next); setSelectorOpen(false); }}
        title="Select model training instrument"
      />
    </section>
  );
}

function AccessPanel() {
  return (
    <div className="rounded-xl border border-loss/20 bg-loss/5 p-5" role="alert">
      <div className="flex items-start gap-3">
        <FiAlertCircle className="mt-0.5 h-5 w-5 flex-none text-loss" />
        <div>
          <p className="font-semibold text-loss">Admin Access Required</p>
          <p className="mt-1 text-sm text-fg-2">
            Model registry access is limited to superusers.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatePanel({ icon, title, message }) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--divider)] bg-bg-2 px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-bg-3 text-fg-3">
        {icon}
      </div>
      <p className="font-semibold text-fg-1">{title}</p>
      {message && <p className="mt-1 text-sm text-fg-3">{message}</p>}
    </div>
  );
}

function DegradedBanner({ testId = "instrument-lookup-degraded" }) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="mb-3 flex items-start gap-2 rounded-xl border border-[var(--divider)] bg-bg-2 p-3 text-sm text-fg-2"
    >
      <FiAlertCircle className="mt-0.5 h-4 w-4 flex-none text-loss" />
      <span>
        <strong className="text-fg-1">Instrument details unavailable.</strong>{" "}
        The instrument service could not be reached, so instrument names are not shown. Registry data below is complete.
      </span>
    </div>
  );
}

export default function ModelsPage() {
  const { user } = useAuth();
  const isSuperuser = user?.is_superuser === true;
  const [status, setStatus] = useState("all");
  const [models, setModels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [instrumentLookupDegraded, setInstrumentLookupDegraded] = useState(false);
  const [loading, setLoading] = useState(isSuperuser);
  const [listError, setListError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [references, setReferences] = useState({ strategies: [], deployments: [] });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [trainingBusy, setTrainingBusy] = useState(false);
  const [trainingJob, setTrainingJob] = useState(null);
  const [trainingError, setTrainingError] = useState(null);
  const [transitionBusy, setTransitionBusy] = useState(false);
  const [transitionError, setTransitionError] = useState(null);
  const listEpoch = useRef(0);
  const detailEpoch = useRef(0);
  const trainingJobRef = useRef(trainingJob);
  const mutationRequestIds = useRef(new Map());
  trainingJobRef.current = trainingJob;

  const loadModels = useCallback(({ showLoading = true } = {}) => {
    if (!isSuperuser) return Promise.resolve([]);
    const epoch = ++listEpoch.current;
    if (showLoading) setLoading(true);
    setListError(null);

    const params = { view: "full" };
    if (status !== "all") params.status = status;

    return services.models.listVersions(params)
      .then((response) => {
        if (listEpoch.current !== epoch) return [];
        const { versions: nextModels, instrumentLookupDegraded: degraded } = unwrapModelVersions(response);
        const payload = unwrapModelPayload(response);
        setModels(nextModels);
        setTrainingTypes(Array.isArray(payload.training_types) ? payload.training_types : []);
        setInstrumentLookupDegraded(degraded);
        setSelectedId((current) => {
          if (current && nextModels.some((entry) => entry.id === current)) return current;
          return nextModels[0]?.id || null;
        });
        return nextModels;
      })
      .catch((error) => {
        if (listEpoch.current !== epoch) return;
        setModels([]);
        setInstrumentLookupDegraded(false);
        setSelectedId(null);
        if (showLoading) setListError(responseStatus(error) === 403 ? "forbidden" : "error");
        return [];
      })
      .finally(() => {
        if (showLoading && listEpoch.current === epoch) setLoading(false);
      });
  }, [isSuperuser, status]);

  useEffect(() => {
    if (!isSuperuser) return undefined;
    loadModels();

    return () => {
      listEpoch.current += 1;
    };
  }, [isSuperuser, loadModels]);

  useEffect(() => {
    if (!trainingJob || trainingJob.completed) return undefined;
    const reconcile = async () => {
      const currentJob = trainingJobRef.current;
      if (!currentJob || currentJob.completed) return;
      const nextModels = await loadModels({ showLoading: false });
      const nextResults = currentJob.results.map((result) => {
        const row = nextModels.find((entry) => (
          entry.id === (result.model_version_id || result.id)
          || (result.bench_id && entry.bench_id === result.bench_id)
          || (result.job_id && entry.job_id === result.job_id)
        ));
        return row ? {
          ...result,
          current_status: row.status,
          head: result.head ?? row.head ?? null,
          model_family: row.model_family,
          verdict: row.verdict || row.scorecard?.verdict,
          verdict_reason: verdictReason(row),
        } : result;
      });
      const completed = nextResults.length > 0 && nextResults.every((result) => (
        TERMINAL_TRAINING_STATUSES.has(result.current_status || result.status)
      ));
      setTrainingJob({ ...currentJob, results: nextResults, completed });
    };
    reconcile();
    const interval = setInterval(reconcile, 3000);
    return () => clearInterval(interval);
  }, [Boolean(trainingJob), trainingJob?.completed, loadModels]);

  const startTraining = async (payload) => {
    const requestKey = `train:${JSON.stringify(payload)}`;
    const stableRequestId = mutationRequestIds.current.get(requestKey) || requestId("model-train");
    mutationRequestIds.current.set(requestKey, stableRequestId);
    setTrainingBusy(true);
    setTrainingError(null);
    try {
      const response = await services.models.train({ ...payload, request_id: stableRequestId });
      const data = unwrapModelPayload(response);
      setTrainingJob({ results: Array.isArray(data.results) ? data.results : [], completed: false });
      mutationRequestIds.current.delete(requestKey);
    } catch (error) {
      setTrainingError(errorMessage(error, "Failed to start model training."));
    } finally {
      setTrainingBusy(false);
    }
  };

  const transitionModel = async (modelVersionId, target) => {
    const requestKey = `transition:${modelVersionId}:${target}`;
    const stableRequestId = mutationRequestIds.current.get(requestKey) || requestId(`model-${target}`);
    mutationRequestIds.current.set(requestKey, stableRequestId);
    setTransitionBusy(true);
    setTransitionError(null);
    try {
      await services.models.transition(modelVersionId, {
        target,
        reason: `admin_${target}`,
        request_id: stableRequestId,
      });
      mutationRequestIds.current.delete(requestKey);
      await loadModels({ showLoading: false });
      if (selectedId === modelVersionId) {
        const response = await services.models.getVersion(modelVersionId);
        const version = unwrapModelPayload(response);
        setDetail({ ...version, transitions: version.transitions || [] });
      }
    } catch (error) {
      setTransitionError(errorMessage(error, "Failed to transition model version."));
    } finally {
      setTransitionBusy(false);
    }
  };

  useEffect(() => {
    if (!isSuperuser || !selectedId) {
      setDetail(null);
      setReferences({ strategies: [], deployments: [] });
      setDetailLoading(false);
      setDetailError(null);
      return undefined;
    }

    const epoch = ++detailEpoch.current;
    setDetailLoading(true);
    setDetailError(null);

    Promise.all([
      services.models.getVersion(selectedId),
      services.models.getReferences(selectedId),
    ])
      .then(([detailResponse, referencesResponse]) => {
        if (detailEpoch.current !== epoch) return;
        const version = unwrapModelPayload(detailResponse);
        setDetail({ ...version, transitions: version.transitions || [] });
        const referencesPayload = unwrapModelPayload(referencesResponse);
        setReferences({
          strategies: referencesPayload.strategies || [],
          deployments: referencesPayload.deployments || [],
        });
      })
      .catch((error) => {
        if (detailEpoch.current !== epoch) return;
        setDetail(null);
        setReferences({ strategies: [], deployments: [] });
        setDetailError(responseStatus(error) === 403 ? "forbidden" : "error");
      })
      .finally(() => {
        if (detailEpoch.current === epoch) setDetailLoading(false);
      });

    return () => {
      detailEpoch.current += 1;
    };
  }, [isSuperuser, selectedId]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-main-bg px-4 pb-10 pt-0 dark:bg-main-dark-bg md:px-6">
      <PageHeader
        title="Models"
        subtitle="Train, evaluate, promote and monitor registry models"
        actions={
          isSuperuser ? (
            <Segmented
              aria-label="Model status filter"
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS.map((value) => ({ value, label: titleCase(value) }))}
            />
          ) : null
        }
      />

      {!isSuperuser ? (
        <AccessPanel />
      ) : (
        <>
        <TrainingPanel trainingTypes={trainingTypes} onTrain={startTraining} busy={trainingBusy} job={trainingJob} error={trainingError} />
        {loading ? (
        <StatePanel icon={<FiClock />} title="Loading models…" />
      ) : listError === "forbidden" ? (
        <StatePanel icon={<FiAlertCircle />} title="Models access denied" message="Your session does not have gateway admin scope." />
      ) : listError ? (
        <StatePanel icon={<FiAlertCircle />} title="Failed to load models" message="The gateway could not load the model registry." />
      ) : models.length === 0 ? (
        <>
          {instrumentLookupDegraded && <DegradedBanner />}
          <StatePanel icon={<FiBox />} title="No models found" message="No registry versions match this status." />
        </>
      ) : (
        <>
          {instrumentLookupDegraded && <DegradedBanner />}
          <div className="overflow-x-auto rounded-xl border border-[var(--divider)] bg-bg-2">
            <DataTable>
              <DataTable.Head>
                <DataTable.Row>
                  <DataTable.HeaderCell>Version</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Type</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Family</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Instrument</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Timeframe</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Status</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Headline metrics</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Training window (IST)</DataTable.HeaderCell>
                  <DataTable.HeaderCell>Created</DataTable.HeaderCell>
                </DataTable.Row>
              </DataTable.Head>
              <DataTable.Body>
                {models.map((entry) => (
                  <DataTable.Row
                    key={entry.id}
                    active={entry.id === selectedId}
                    className={isQualified(entry) ? "bg-profit/5" : undefined}
                    data-testid={isQualified(entry) ? `qualified-model-${entry.id}` : undefined}
                  >
                    <DataTable.Cell>
                      <button
                        type="button"
                        className="font-semibold text-[var(--badge-role-fg)] hover:underline"
                        onClick={() => setSelectedId(entry.id)}
                      >
                        {entry.version_label}
                      </button>
                    </DataTable.Cell>
                    <DataTable.Cell>{capabilityForVersion(entry, trainingTypes)?.title || "—"}</DataTable.Cell>
                    <DataTable.Cell>{titleCase(entry.model_family)}</DataTable.Cell>
                    <DataTable.Cell>
                      {entry.instrument_symbol ? (
                        <span>
                          {entry.instrument_symbol}
                          {entry.instrument_exchange ? ` / ${entry.instrument_exchange}` : ""}
                        </span>
                      ) : (
                        <span className="text-fg-3">Unavailable</span>
                      )}
                    </DataTable.Cell>
                    <DataTable.Cell>{entry.timeframe}</DataTable.Cell>
                    <DataTable.Cell>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={entry.status} />
                        {isQualified(entry) && <Badge variant="profit" size="xs">Qualified</Badge>}
                        {showsDrift(entry.performance) && (
                          <Badge variant="loss" size="xs" dot>Drift</Badge>
                        )}
                      </div>
                      {verdictReason(entry) && !isQualified(entry) && (
                        <p className="mt-1 text-xs text-loss">{titleCase(verdictReason(entry))}</p>
                      )}
                    </DataTable.Cell>
                    <DataTable.Cell><HeadlineMetrics scorecard={entry.scorecard} /></DataTable.Cell>
                    <DataTable.Cell>
                      {formatDate(entry.train_window_start)} – {formatDate(entry.train_window_end)}
                    </DataTable.Cell>
                    <DataTable.Cell>{formatDate(entry.created_at)}</DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable>
          </div>

          {detailLoading ? (
            <div className="mt-6">
              <StatePanel icon={<FiTrendingUp />} title="Loading model details…" />
            </div>
          ) : detailError === "forbidden" ? (
            <div className="mt-6">
              <StatePanel icon={<FiAlertCircle />} title="Model details access denied" />
            </div>
          ) : detailError ? (
            <div className="mt-6">
              <StatePanel icon={<FiAlertCircle />} title="Failed to load model details" />
            </div>
          ) : (
            <ModelDetailPanel
              model={detail}
              references={references}
              onTransition={transitionModel}
              transitionBusy={transitionBusy}
              transitionError={transitionError}
              trainingTypes={trainingTypes}
            />
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}
